import { useState } from 'react';
import { Box, Container, Flex, Heading, HStack, Stack, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { CtaButton } from '../components/CtaButton';

// Sampled from Figma: a magenta bloom left-of-centre at mid-low height
// (peak ~rgb(80,45,62) over the #270D17 base) and a softer, cooler haze on the right.
const SECTION_GLOW =
  'radial-gradient(48% 62% at 18% 55%, rgba(140,85,120,0.42), transparent 62%),' +
  'radial-gradient(46% 58% at 86% 50%, rgba(96,92,98,0.24), transparent 64%)';

// Recommended (BUSINESS) card: cream→warm diagonal (Figma ~140deg), no border.
const CARD_CREAM = 'linear(140deg, #FFFAF9 22%, #FBE5D8 83%)';
// Recommended badge pill: deep-purple → rose gradient (Figma).
const BADGE_GRADIENT = 'linear(120deg, #512A59 18%, #824C68 85%)';

const TIERS = [
  { key: 'start', recommended: false },
  { key: 'business', recommended: true },
  { key: 'pro', recommended: false },
] as const;

type Billing = 'monthly' | 'yearly';

// Lightning-bolt bullet (Figma "mdi:thunder"), exact vector from the design.
function BoltIcon({ color }: { color: string }) {
  return (
    <Box as="svg" viewBox="0 0 11 24" w="9px" h="20px" mt="2px" flexShrink={0} color={color} aria-hidden>
      <path
        d="M4.9475 15.0499C4.97606 14.4788 4.52065 14 3.94875 14L1.61803 14C0.874652 14 0.391156 13.2177 0.723607 12.5528L4.62986 4.74029C5.12415 3.75169 6.62025 4.17729 6.52018 5.27803L6.09914 9.90946C6.0459 10.4951 6.507 11 7.09503 11L9.38197 11C10.1253 11 10.6088 11.7823 10.2764 12.4472L6.63115 19.7377C6.14701 20.706 4.68391 20.3218 4.73797 19.2405L4.9475 15.0499Z"
        fill="currentColor"
      />
    </Box>
  );
}

function BillingToggle({ value, onChange }: { value: Billing; onChange: (v: Billing) => void }) {
  const { t } = useTranslation();
  const optBase = {
    px: '18px',
    h: '36px',
    borderRadius: '6px',
    fontSize: 'sm',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'all 0.15s',
  } as const;
  return (
    <HStack
      spacing="4px"
      bg="rgba(251,245,239,0.10)"
      border="1px solid"
      borderColor="rgba(251,245,239,0.18)"
      borderRadius="10px"
      p="4px"
    >
      <Box
        as="button"
        type="button"
        {...optBase}
        bg={value === 'monthly' ? 'onboarding.cream' : 'transparent'}
        color={value === 'monthly' ? 'onboarding.deepPlum' : 'whiteAlpha.800'}
        onClick={() => onChange('monthly')}
      >
        {t('landing.pricing.billing.monthly')}
      </Box>
      <Box
        as="button"
        type="button"
        {...optBase}
        bg={value === 'yearly' ? 'onboarding.cream' : 'transparent'}
        color={value === 'yearly' ? 'onboarding.deepPlum' : 'whiteAlpha.800'}
        onClick={() => onChange('yearly')}
      >
        {t('landing.pricing.billing.yearly')}
        <Text as="span" ml="6px" fontSize="xs" color={value === 'yearly' ? 'onboarding.roseDeep' : 'onboarding.warmLight'}>
          {t('landing.pricing.billing.discount')}
        </Text>
      </Box>
    </HStack>
  );
}

function TierCard({ tierKey, recommended, billing }: { tierKey: string; recommended: boolean; billing: Billing }) {
  const { t } = useTranslation();
  const base = `landing.pricing.tiers.${tierKey}`;
  const price = t(`${base}.${billing === 'yearly' ? 'priceYearly' : 'price'}`);
  const features = t(`${base}.features`, { returnObjects: true }) as string[];

  // The recommended (BUSINESS) tier is a solid cream card; the others are dark glass.
  const cream = recommended;
  const titleColor = cream ? 'onboarding.deepPlum' : 'white';

  return (
    <Stack
      flex="1"
      minW={0}
      spacing="20px"
      p={{ base: '28px', lg: '32px' }}
      borderRadius="10px"
      bgGradient={cream ? CARD_CREAM : undefined}
      bg={cream ? undefined : 'rgba(251,245,239,0.10)'}
      border={cream ? 'none' : '1px solid'}
      borderColor={cream ? undefined : 'onboarding.midRose2'}
      backdropFilter="blur(14px)"
      position="relative"
      boxShadow="none"
    >
      <Flex justify="space-between" align="center" minH="22px">
        <Text fontSize="20px" fontWeight={600} color={titleColor}>
          {t(`${base}.name`)}
        </Text>
        {recommended && (
          <Text
            as="span"
            fontSize="xs"
            fontWeight={500}
            color="white"
            bgGradient={BADGE_GRADIENT}
            px="12px"
            py="4px"
            borderRadius="pill"
          >
            {t('landing.pricing.recommended')}
          </Text>
        )}
      </Flex>

      <Flex align="baseline" gap="6px">
        <Text fontFamily="heading" fontSize="40px" fontWeight={600} color={titleColor} lineHeight={1}>
          {price}
        </Text>
        <Text fontSize="xs" color={cream ? 'onboarding.roseFaint' : 'whiteAlpha.600'}>
          {t('landing.pricing.perMonth')}
        </Text>
      </Flex>

      <Text fontSize="xs" color={cream ? 'onboarding.deepPlum' : 'whiteAlpha.700'} opacity={cream ? 0.7 : 1} minH="32px">
        {t(`${base}.tagline`)}
      </Text>

      <CtaButton
        to="/onboarding"
        w="100%"
        height="48px"
        borderRadius="pill"
        fontSize="md"
        fontWeight={600}
        bg={recommended ? 'onboarding.plum' : 'transparent'}
        color="onboarding.cream"
        border={recommended ? 'none' : '1px solid'}
        borderColor={recommended ? undefined : 'onboarding.cream'}
        _hover={{ bg: recommended ? 'onboarding.roseDeep' : 'whiteAlpha.200' }}
        withArrow={recommended}
        trackEventName="cta_click"
        trackProps={{ section: 'pricing', tier: tierKey }}
      >
        {t('landing.pricing.cta')}
      </CtaButton>

      <Stack spacing="12px" pt="4px">
        {features.map((f) => (
          <HStack key={f} align="flex-start" spacing="10px">
            <BoltIcon color={cream ? 'onboarding.deepPlum' : 'white'} />
            <Text fontSize="sm" color={cream ? 'onboarding.deepPlum' : 'whiteAlpha.900'} lineHeight={1.4}>
              {f}
            </Text>
          </HStack>
        ))}
      </Stack>
    </Stack>
  );
}

export function Pricing() {
  const { t } = useTranslation();
  const [billing, setBilling] = useState<Billing>('yearly');

  return (
    <Box as="section" id="pricing" position="relative" overflow="hidden" bg="#270D17" color="white">
      {/* Diffuse colour field (sampled from Figma) sits at the bottom. */}
      <Box position="absolute" inset={0} backgroundImage={SECTION_GLOW} pointerEvents="none" />
      {/* Wavy line texture (Figma: two halves at 50% opacity), painted as background
          images on absolute boxes so they never participate in section height. */}
      <Box
        position="absolute"
        top={0}
        bottom={0}
        left={0}
        w="50%"
        opacity={0.5}
        pointerEvents="none"
        bgImage="url(/images/landing/pricing-wave-l.png)"
        bgSize="cover"
        bgPosition="left center"
        bgRepeat="no-repeat"
      />
      <Box
        position="absolute"
        top={0}
        bottom={0}
        right={0}
        w="50%"
        opacity={0.5}
        pointerEvents="none"
        bgImage="url(/images/landing/pricing-wave-r.png)"
        bgSize="cover"
        bgPosition="right center"
        bgRepeat="no-repeat"
      />
      <Container maxW="1240px" py={{ base: 16, lg: 24 }} position="relative">
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          gap={6}
          mb={{ base: 10, lg: 14 }}
        >
          <Heading
            as="h2"
            fontFamily="heading"
            fontWeight={700}
            fontSize="display.lg"
            lineHeight={1.1}
            letterSpacing="-0.01em"
            color="white"
          >
            {t('landing.pricing.title')}
          </Heading>
          <BillingToggle value={billing} onChange={setBilling} />
        </Flex>

        <Flex direction={{ base: 'column', lg: 'row' }} gap="24px" align="stretch">
          {TIERS.map((tier) => (
            <TierCard key={tier.key} tierKey={tier.key} recommended={tier.recommended} billing={billing} />
          ))}
        </Flex>
      </Container>
    </Box>
  );
}
